import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button as MuiButton, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import Page from '../../../components/atoms/page';
import TableWidget, { TableColumn, TableRowData } from '../../../components/molecules/tableWidget';
import { FetchFromBackoffice } from '../../../utils/requests';
import EditVariant from './editVariant';
import CreateVariant from './createVariant';
import CreateModel from './createModel';
import CreateWmi from './createWmi';
import CreateBrand from './createBrand';
import { VehicleBrand, VehicleModel, VehicleVariant } from './types';

type VehicleVariantRow = TableRowData & VehicleVariant & {
  yearRange: string;
};

const VehicleTypes = () => {
  const { isLoading } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState<VehicleBrand[]>([]);
  const [models, setModels] = useState<VehicleModel[]>([]);
  const [variants, setVariants] = useState<VehicleVariant[]>([]);
  const [loading, setLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingVariant, setEditingVariant] = useState<VehicleVariant | null>(null);
  const [creatingVariant, setCreatingVariant] = useState(false);
  const [creatingModel, setCreatingModel] = useState(false);
  const [creatingWmi, setCreatingWmi] = useState(false);
  const [creatingBrand, setCreatingBrand] = useState(false);

  const brandParam = searchParams.get('brandId');
  const modelParam = searchParams.get('modelId');
  const selectedBrandId = brandParam ? Number(brandParam) : '';
  const selectedModelId = modelParam ? Number(modelParam) : '';

  const loadBrands = async () => {
    try {
      setError(null);
      const data = await FetchFromBackoffice<VehicleBrand[]>('portal/vehicle/brands');
      setBrands(data ?? []);
    } catch (loadError) {
      console.error('Error fetching vehicle brands', loadError);
      setError('Unable to load data.');
    } finally {
      setBrandsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      loadBrands();
    }
  }, [isLoading]);

  useEffect(() => {
    setModels([]);
    setVariants([]);

    if (selectedBrandId === '') {
      setModelsLoading(false);
      return;
    }

    const loadModels = async () => {
      setModelsLoading(true);
      setError(null);

      try {
        const data = await FetchFromBackoffice<VehicleModel[]>(
          `portal/vehicle/brands/${selectedBrandId}/models`
        );
        setModels(data ?? []);
      } catch (loadError) {
        console.error('Error fetching vehicle models', loadError);
        setError('Unable to load data.');
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
  }, [selectedBrandId]);

  useEffect(() => {
    if (selectedBrandId === '' || selectedModelId === '') {
      setVariants([]);
      return;
    }

    const loadVariants = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await FetchFromBackoffice<VehicleVariant[]>(
          `portal/vehicle/brands/${selectedBrandId}/models/${selectedModelId}/variants`
        );
        setVariants(data ?? []);
      } catch (loadError) {
        console.error('Error fetching vehicle variants', loadError);
        setError('Unable to load data.');
        setVariants([]);
      } finally {
        setLoading(false);
      }
    };

    loadVariants();
  }, [selectedBrandId, selectedModelId]);

  const tableData = useMemo<VehicleVariantRow[]>(
    () => variants.map((variant) => ({
      ...variant,
      id: String(variant.variantId),
      yearRange: variant.toYear ? `${variant.fromYear} - ${variant.toYear}` : `${variant.fromYear} - ON`,
    })),
    [variants]
  );

  const selectedBrand = brands.find((brand) => brand.brandId === selectedBrandId);
  const selectedModel = models.find((model) => model.modelId === selectedModelId);

  if (isLoading) {
    return <Box>Loading ...</Box>;
  }

  return (
    <Page
      title="Vehicle variants"
      linkTitle={selectedModelId !== '' ? 'add variant' : undefined}
      onClickLink={() => setCreatingVariant(true)}
      linkDisabled={selectedModelId === ''}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2, mt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={brandsLoading}>
          <InputLabel>Brand</InputLabel>
          <Select
            value={selectedBrandId}
            label="Brand"
            onChange={(event) => setSearchParams({ brandId: String(event.target.value) })}
          >
            {brands.map((brand) => (
              <MenuItem key={brand.brandId} value={brand.brandId}>{brand.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <MuiButton
          variant="outlined"
          size="small"
          onClick={() => setCreatingBrand(true)}
        >
          Add brand
        </MuiButton>
        <MuiButton
          variant="outlined"
          size="small"
          disabled={selectedBrandId === ''}
          onClick={() => setCreatingWmi(true)}
        >
          Add WMI
        </MuiButton>
        <FormControl size="small" sx={{ minWidth: 280 }} disabled={selectedBrandId === '' || modelsLoading}>
          <InputLabel>Model</InputLabel>
          <Select
            value={selectedModelId}
            label="Model"
            onChange={(event) => setSearchParams({
              brandId: String(selectedBrandId),
              modelId: String(event.target.value),
            })}
          >
            {models.map((model) => (
              <MenuItem key={model.modelId} value={model.modelId}>{model.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <MuiButton
          variant="outlined"
          size="small"
          disabled={selectedBrandId === ''}
          onClick={() => setCreatingModel(true)}
        >
          Add model
        </MuiButton>
      </Box>
      {selectedModelId !== '' ? (
        loading ? <Box sx={{ py: 3 }}>Loading vehicle variants ...</Box> : (
          <TableWidget
            data={tableData}
            columns={[
              { id: 'name', label: 'Variant' },
              { id: 'powertrain', label: 'Powertrain' },
              { id: 'yearRange', label: 'Years' },
              { id: 'weight', label: 'Weight (kg)' },
              { id: 'length', label: 'Length (mm)' },
              { id: 'width', label: 'Width (mm)' },
              { id: 'height', label: 'Height (mm)' },
              { id: 'externalId', label: 'MonitorId' },
            ]}
            defaultSort="name"
            onEdit={(_event, id) => {
              const variant = variants.find((item) => String(item.variantId) === id);
              setEditingVariant(variant ?? null);
            }}
            themes={{ device: 'desktop' }}
            pagination
          />
        )
      ) : <Box sx={{ py: 3 }}>Select a brand and model to view variants.</Box>}
      <EditVariant
        variant={editingVariant}
        brandId={selectedBrandId}
        brandName={selectedBrand?.name ?? ''}
        brandPatterns={selectedBrand?.patterns ?? []}
        modelName={selectedModel?.name ?? ''}
        open={editingVariant !== null}
        onClose={() => setEditingVariant(null)}
        onSaved={(updatedVariant) => setVariants((current) => current.map((variant) => (
          variant.variantId === updatedVariant.variantId ? updatedVariant : variant
        )))}
      />
      <CreateVariant
        brandId={selectedBrandId}
        modelId={selectedModelId}
        brandName={selectedBrand?.name ?? ''}
        brandPatterns={selectedBrand?.patterns ?? []}
        modelName={selectedModel?.name ?? ''}
        open={creatingVariant}
        onClose={() => setCreatingVariant(false)}
        onCreated={(createdVariant) => setVariants((current) => [...current, createdVariant])}
      />
      <CreateModel
        brandId={selectedBrandId}
        brandName={selectedBrand?.name ?? ''}
        open={creatingModel}
        onClose={() => setCreatingModel(false)}
        onCreated={(createdModel) => {
          setModels((current) => [...current, createdModel]);
          setSearchParams({
            brandId: String(selectedBrandId),
            modelId: String(createdModel.modelId),
          });
        }}
      />
      <CreateWmi
        brandId={selectedBrandId}
        brandName={selectedBrand?.name ?? ''}
        open={creatingWmi}
        onClose={() => setCreatingWmi(false)}
        onCreated={(code) => {
          setBrands((current) => current.map((brand) => (
            brand.brandId === selectedBrandId
              ? { ...brand, patterns: [...(brand.patterns ?? []), code] }
              : brand
          )));
        }}
      />
      <CreateBrand
        open={creatingBrand}
        onClose={() => {
          setCreatingBrand(false);
        }}
        onCreated={(createdBrand) => {
          setBrands((current) => [...current, createdBrand]);
          setSearchParams({ brandId: String(createdBrand.brandId) });
          setCreatingBrand(false);
        }}
      />
    </Page>
  );
};

export default VehicleTypes;
